// src/serviceWorkerRegistration.js
// Registers the service worker (production builds only) and tells the app when a new version is waiting.
import { isNativeApp } from './native';

let waiting = null;
const listeners = new Set();

// cb(waitingWorker | null) is called now and whenever the state changes; returns the function to stop listening.
export function subscribeToUpdates(callback) {
    listeners.add(callback);
    callback(waiting);
    return () => listeners.delete(callback);
}

// For tests: forget a waiting version.
export function resetUpdates() {
    waiting = null;
    listeners.clear();
}

function setWaiting(worker) {
    waiting = worker;
    listeners.forEach((callback) => callback(waiting));
}

// Whether a service worker that just finished installing is an update (there is a page it would replace) rather
// than the very first install.
export function watchForUpdates(registration, hasController = () => Boolean(navigator.serviceWorker.controller)) {
    if (registration.waiting && hasController()) setWaiting(registration.waiting);
    registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && hasController()) setWaiting(installing);
        });
    });
}

// Installs the waiting version and reloads once it has taken over.
export function applyUpdate() {
    if (!waiting) return;
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
    });
    waiting.postMessage({ type: 'SKIP_WAITING' });
}

const CHECK_EVERY_MS = 30 * 60 * 1000; // a phone left open all day still finds out about a new version

export function register() {
    if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) return;
    if (isNativeApp()) return; // inside the Android/iOS app the files come with the app: nothing to keep or update
    window.addEventListener('load', () => {
        navigator.serviceWorker
            .register(`${process.env.PUBLIC_URL}/service-worker.js`)
            .then((registration) => {
                watchForUpdates(registration);
                const check = () => registration.update().catch(() => {}); // offline: try again later
                setInterval(check, CHECK_EVERY_MS);
                document.addEventListener('visibilitychange', () => {
                    if (document.visibilityState === 'visible') check();
                });
            })
            .catch((error) => console.error('Could not register the service worker:', error));
    });
}

// Drops the service worker and everything it cached (the app files and the offline engine), then reloads so the
// newest version is fetched. Tournaments, sign-in and the offline copy of the data are not touched.
export async function clearAppCache() {
    try {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((registration) => registration.unregister()));
        }
        if (typeof caches !== 'undefined') {
            const names = await caches.keys();
            await Promise.all(names.map((name) => caches.delete(name)));
        }
    } finally {
        window.location.reload();
    }
}
